import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useSiteSettings } from '../contexts/SiteSettingsContext';


import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Send,
  CheckCircle,
  Building2
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const staticContactInfo = [
  {
    icon: Clock,
    title: 'Business Hours',
    content: 'Monday - Friday\n9:00 AM - 6:00 PM EST'
  }
];

const businessIdentifiers = [
  { label: 'DUNS', value: 'Available upon request' },
  { label: 'UEI', value: 'Available upon request' },
  { label: 'CAGE CODE', value: 'Available upon request' }
];

export const Contact = ({ onDemoClick }) => {
    const { siteSettings } = useSiteSettings();
  
  // Build contactInfo dynamically using siteSettings
  const contactInfo = [
    {
      icon: MapPin,
      title: 'Headquarters',
      content: siteSettings?.address ? siteSettings.address.replace(/<[^>]*>/g, '') : '111 N Orange Ave Suite 850\nOrlando, FL 32801'
    },
    {
      icon: Phone,
      title: 'Phone',
      content: siteSettings?.contact_number || '407-777-2216',
      link: `tel:${siteSettings?.contact_number || '407-777-2216'}`
    },
    {
      icon: Mail,
      title: 'Email',
      content: siteSettings?.contact_email || 'info@vetforcesolutions.com',
      link: `mailto:${siteSettings?.contact_email || 'info@vetforcesolutions.com'}`
    },
    ...staticContactInfo,
    // Append additional items from siteSettings
    ...(siteSettings?.secondary_contact_number ? [{
      icon: Phone,
      title: 'Secondary Phone',
      content: siteSettings.secondary_contact_number,
      link: `tel:${siteSettings.secondary_contact_number}`
    }] : []),
    ...(siteSettings?.secondary_email ? [{
      icon: Mail,
      title: 'Secondary Email',
      content: siteSettings.secondary_email,
      link: `mailto:${siteSettings.secondary_email}`
    }] : []),
    ...(siteSettings?.address2 ? [{
      icon: MapPin,
      title: 'Secondary Address',
      content: siteSettings.address2
    }] : [])
  ];
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      console.log('Submitting demo request with data:', JSON.stringify(formData, null, 2));
      
      await axios.post(`https://vetforcesolutions.com/backend/api/hiring_requests/contact_request_submit`, formData);
      setIsSuccess(true);
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: ''
      });
    } catch (err) {
      setError('Failed to submit your message. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#090B17] pt-24" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-[#8F262B]/5 to-transparent" />
        <div className="w-full px-6 sm:px-12 lg:px-20 relative">
          <div className="text-center max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="section-label">Contact Us</span>
              <h1 className="text-4xl sm:text-5xl font-outfit font-bold text-silver-shine mt-4 mb-6" data-testid="contact-title">
                Get In <span className="text-gradient">Touch</span>
              </h1>
              <p className="text-[#C9C9CD] text-lg">
                Ready to accelerate your clinical research? Contact our team to discuss 
                how VetForce Solutions can support your next project.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl font-outfit font-bold text-silver-shine mb-6">
                Send Us a Message
              </h2>

              {isSuccess ? (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="card-gradient p-8 text-center"
                >
                  <div className="w-16 h-16 bg-teal-500/20 border border-teal-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-[#8F262B]" />
                  </div>
                  <h3 className="text-xl font-semibold text-silver-shine mb-2">Message Sent!</h3>
                  <p className="text-[#C9C9CD] mb-6">
                    Thank you for reaching out. Our team will respond within 24 hours.
                  </p>
                  <button 
                    onClick={() => setIsSuccess(false)}
                    className="btn-outline px-6 py-2"
                  >
                    Send Another Message
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  {error && (
                    <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-sm">
                      {error}
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="form-label">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="form-input"
                        required
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label className="form-label">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="form-input"
                        required
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="form-label">Company</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="form-input"
                        data-testid="contact-company-input"
                      />
                    </div>
                    <div>
                      <label className="form-label">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="form-input"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="form-label">Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className="form-input resize-none"
                      placeholder="Tell us about your project or inquiry..."
                      required
                      data-testid="contact-message-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-gradient w-full py-4 flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Message
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-2xl font-outfit font-bold text-silver-shine mb-6">
                  Contact Information
                </h2>
                <div className="space-y-4">
                  {contactInfo.map((item) => (
                    <div key={item.title} className="card-dark p-5">
                      <div className="flex items-start gap-4">
                        <div className="icon-box w-10 h-10 flex-shrink-0">
                          <item.icon className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="text-silver-shine font-semibold mb-1">{item.title}</h3>
                          {item.link ? (
                            <a 
                              href={item.link}
                              className="text-[#C9C9CD] hover:text-[#8F262B] transition-colors"
                              data-testid={`contact-info-${item.title.toLowerCase()}`}
                            >
                              {item.content}
                            </a>
                          ) : (
                            <p className="text-[#C9C9CD] whitespace-pre-line">{item.content}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Business Identifiers */}
              <div>
                <h3 className="text-lg font-outfit font-semibold text-silver-shine mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#8F262B]" />
                  Business Identifiers
                </h3>
                <div className="card-dark p-5">
                  <div className="space-y-3">
                    {businessIdentifiers.map((item) => (
                      <div key={item.label} className="flex justify-between items-center">
                        <span className="text-[#9F999F]">{item.label}</span>
                        <span className="text-[#C9C9CD] font-mono text-sm">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="card-gradient p-6">
                <h3 className="text-silver-shine font-semibold mb-3">Need Immediate Assistance?</h3>
                <p className="text-[#C9C9CD] text-sm mb-4">
                  Schedule a consultation with our team to discuss your clinical research needs.
                </p>
                <button 
                  onClick={onDemoClick}
                  className="btn-gradient w-full py-3"
                  data-testid="contact-demo-btn"
                >
                  Request a Demo
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 section-surface border-t border-[#2E3446]">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <div className="text-center mb-12">
            <h2 className="section-title">Our <span className="text-gradient">Location</span></h2>
            <p className="text-[#C9C9CD] mt-4">
              Visit us at our Orlando headquarters
            </p>
          </div>

          <div className="aspect-video rounded-2xl overflow-hidden border border-[#2E3446] glow-subtle">
            <iframe
              src={siteSettings?.google_maps || "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.8126326543093!2d-81.3792362845872!3d28.54166598245971!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e77a4e7f4f6bdf%3A0x8c8e4a5f4e4b5b6!2s111%20N%20Orange%20Ave%20%23850%2C%20Orlando%2C%20FL%2032801%2C%20USA!5e0!3m2!1sen!2sin!4v1696223456789!5m2!1sen!2sin"}
              width="100%"
              height="100%"
              style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg)' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="VetForce Solutions Location"
            />
          </div>
        </div>
      </section>
    </div>
  );
};
